// @figma file=pkCGby54zXzTsccXvHqzO6 node=3996:9271 name="Input Number" set="mytaible-shadcn-studio" verified=2026-03-22

import { ChevronDown, ChevronUp, Minus, Plus } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Visual layout variant for InputNumber.
 *
 * | Variant | Layout |
 * |---|---|
 * | `split` | − on left, + on right (default) |
 * | `split-rounded` | same, circular buttons |
 * | `end` | input left, − + side-by-side on right |
 * | `end-rounded` | same, circular buttons |
 * | `stacked` | input left, + over − stacked on right |
 * | `stacked-chevrons` | stacked with ↑ / ↓ icons |
 */
export type InputNumberVariant =
  | 'split'
  | 'split-rounded'
  | 'end'
  | 'end-rounded'
  | 'stacked'
  | 'stacked-chevrons';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface InputNumberProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    'type' | 'onChange' | 'value' | 'defaultValue'
  > {
  /** Controlled numeric value. Pair with `onChange`. */
  value?: number;
  /** Initial value for uncontrolled usage. Defaults to `0`. */
  defaultValue?: number;
  /**
   * Fired with the new numeric value after each stepper click or direct edit.
   * The emitted value is always clamped to `[min, max]`.
   */
  onChange?: (value: number) => void;
  /** Minimum allowed value — decrement button disables at this boundary. */
  min?: number;
  /** Maximum allowed value — increment button disables at this boundary. */
  max?: number;
  /** Step size per click or keyboard Up/Down. Defaults to `1`. */
  step?: number;
  /** Disables the entire control — input and both stepper buttons. */
  disabled?: boolean;
  /** Additional CSS classes merged onto the outermost wrapper `<div>`. */
  className?: string;
  /** Visual layout variant. Defaults to `'split'`. */
  variant?: InputNumberVariant;
}

// ─── Component ────────────────────────────────────────────────────────────────

const InputNumber = React.forwardRef<HTMLInputElement, InputNumberProps>(
  (
    {
      value: valueProp,
      defaultValue,
      onChange,
      min,
      max,
      step = 1,
      disabled = false,
      variant = 'split',
      className,
      ...props
    },
    ref,
  ) => {
    // ── Controlled / Uncontrolled ─────────────────────────────────────────────
    const isControlled = valueProp !== undefined;
    const [internalValue, setInternalValue] = React.useState<number>(defaultValue ?? 0);
    const current = isControlled ? (valueProp as number) : internalValue;

    // ── Value clamping ────────────────────────────────────────────────────────
    const clamp = React.useCallback(
      (n: number): number => {
        let v = n;
        if (min !== undefined) v = Math.max(min, v);
        if (max !== undefined) v = Math.min(max, v);
        return v;
      },
      [min, max],
    );

    // ── Commit new value ──────────────────────────────────────────────────────
    const commit = React.useCallback(
      (next: number) => {
        const clamped = clamp(next);
        if (!isControlled) setInternalValue(clamped);
        onChange?.(clamped);
      },
      [clamp, isControlled, onChange],
    );

    // ── Input change (direct edit) ────────────────────────────────────────────
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const parsed = parseFloat(e.target.value);
      if (!Number.isNaN(parsed)) commit(parsed);
    };

    // ── Boundary flags ────────────────────────────────────────────────────────
    const atMin = min !== undefined && current <= min;
    const atMax = max !== undefined && current >= max;

    // ── Variant flags ─────────────────────────────────────────────────────────
    const isRounded = variant === 'split-rounded' || variant === 'end-rounded';
    const isEnd = variant === 'end' || variant === 'end-rounded';
    const isStacked = variant === 'stacked' || variant === 'stacked-chevrons';
    const useChevrons = variant === 'stacked-chevrons';

    // ── Icons ─────────────────────────────────────────────────────────────────
    const IncrIcon = useChevrons ? ChevronUp : Plus;
    const DecrIcon = useChevrons ? ChevronDown : Minus;

    // ── Button classes ────────────────────────────────────────────────────────
    const btnBase = cn(
      'flex shrink-0 items-center justify-center',
      'text-muted-foreground transition-colors',
      'hover:bg-muted hover:text-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
      'disabled:pointer-events-none disabled:opacity-50',
    );

    // Full-height flat button
    const flatBtnCls = cn(btnBase, 'h-9 w-9');
    // Circular button — floats inside the container
    const roundedBtnCls = cn(btnBase, 'size-7 rounded-full m-1');
    // Half-height button for stacked layout (does not use shrink-0 so flex-1 wins)
    const stackedBtnCls = cn(
      'flex flex-1 items-center justify-center w-8',
      'text-muted-foreground transition-colors',
      'hover:bg-muted hover:text-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
      'disabled:pointer-events-none disabled:opacity-50',
    );

    const activeBtnCls = isRounded ? roundedBtnCls : flatBtnCls;

    // ── Input classes ─────────────────────────────────────────────────────────
    const inputCls = cn(
      'h-9 w-16 shrink-0',
      'text-center text-base text-foreground sm:text-sm',
      'bg-transparent px-2',
      // Split: flanked by buttons → need both side borders
      // End / Stacked: input is leftmost → only right border needed
      isEnd || isStacked ? 'border-r border-input' : 'border-x border-input',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
      'focus-visible:ring-offset-2 focus-visible:ring-offset-background',
      '[appearance:textfield]',
      '[&::-webkit-inner-spin-button]:appearance-none',
      '[&::-webkit-outer-spin-button]:appearance-none',
      'disabled:cursor-not-allowed',
    );

    // ── Wrapper classes ───────────────────────────────────────────────────────
    const wrapperCls = cn(
      'inline-flex items-center overflow-hidden',
      'rounded-[var(--field-radius,var(--radius))] border border-input bg-input-bg shadow-sm',
      disabled && 'opacity-50',
      className,
    );

    // ── Shared input element ──────────────────────────────────────────────────
    const inputEl = (
      <input
        ref={ref}
        type="number"
        inputMode="numeric"
        value={current}
        min={min}
        max={max}
        step={step}
        disabled={disabled}
        onChange={handleInputChange}
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={current}
        className={inputCls}
        {...props}
      />
    );

    // ── Stacked layout ────────────────────────────────────────────────────────
    if (isStacked) {
      return (
        <div className={wrapperCls}>
          {inputEl}
          <div className="flex flex-col self-stretch border-l border-input">
            <button
              type="button"
              onClick={() => commit(current + step)}
              disabled={disabled || atMax}
              aria-label="Increment"
              className={stackedBtnCls}
            >
              <IncrIcon className="size-3" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => commit(current - step)}
              disabled={disabled || atMin}
              aria-label="Decrement"
              className={cn(stackedBtnCls, 'border-t border-input')}
            >
              <DecrIcon className="size-3" aria-hidden="true" />
            </button>
          </div>
        </div>
      );
    }

    // ── End layout ────────────────────────────────────────────────────────────
    if (isEnd) {
      return (
        <div className={wrapperCls}>
          {inputEl}
          <button
            type="button"
            onClick={() => commit(current - step)}
            disabled={disabled || atMin}
            aria-label="Decrement"
            className={cn(activeBtnCls, !isRounded && 'border-r border-input')}
          >
            <DecrIcon className="size-4" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => commit(current + step)}
            disabled={disabled || atMax}
            aria-label="Increment"
            className={activeBtnCls}
          >
            <IncrIcon className="size-4" aria-hidden="true" />
          </button>
        </div>
      );
    }

    // ── Split layout (default) ────────────────────────────────────────────────
    return (
      <div className={wrapperCls}>
        <button
          type="button"
          onClick={() => commit(current - step)}
          disabled={disabled || atMin}
          aria-label="Decrement"
          className={activeBtnCls}
        >
          <DecrIcon className="size-4" aria-hidden="true" />
        </button>
        {inputEl}
        <button
          type="button"
          onClick={() => commit(current + step)}
          disabled={disabled || atMax}
          aria-label="Increment"
          className={activeBtnCls}
        >
          <IncrIcon className="size-4" aria-hidden="true" />
        </button>
      </div>
    );
  },
);

InputNumber.displayName = 'InputNumber';

// ─── Exports ──────────────────────────────────────────────────────────────────

export { InputNumber };
